import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { activitiesAPI } from '../api/client';
import { Footprints, Coffee, Mountain, Bike, UtensilsCrossed, Landmark, MapPin, Search } from 'lucide-react';

import Layout from '../components/Layout';
import '../styles/activities.css';

const ACTIVITY_ICONS = {
  run: Footprints,
  coffee: Coffee,
  hike: Mountain,
  cycling: Bike,
  dinner: UtensilsCrossed,
  museum: Landmark,
  other: MapPin,
};

function ActivityIcon({ type, size = 20, color = '#52AAD8' }) {
  const Icon = ACTIVITY_ICONS[type] || MapPin;
  return <Icon size={size} color={color} strokeWidth={2} />;
}

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  return (parts.length > 1 ? parts[0][0] + parts[parts.length - 1][0] : name[0]).toUpperCase();
}

function timeAgo(dateString) {
  if (!dateString) return '';
  const mins = Math.floor((Date.now() - new Date(dateString)) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function formatDate(d) {
  if (!d) return 'Anytime';
  return new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function Activities() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const currentUserId = localStorage.getItem('userId');

  useEffect(() => {
    activitiesAPI.getFeed()
      .then(r => setActivities(r.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleRSVP = async (e, activityId, type) => {
    e.stopPropagation();
    await activitiesAPI.rsvp(activityId, type).catch(console.error);
    activitiesAPI.getFeed().then(r => setActivities(r.data || [])).catch(() => {});
  };

  const filtered = activities.filter(a =>
    !search ||
    (a.title || a.activity_type || '').toLowerCase().includes(search.toLowerCase()) ||
    (a.location_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (a.creator_display_name || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Layout title="Activities">
      <div className="act-feed-page">
        <div className="act-feed-header">
          <div>
            <h2 className="act-feed-title">Activity Feed</h2>
            <p className="act-feed-sub">Plans from your crew</p>
          </div>
          <button className="btn-primary" style={{ borderRadius: 12 }} onClick={() => navigate('/create-activity')}>+ New Activity</button>
        </div>

        <div className="act-search-wrap">
          <Search size={16} className="act-search-icon" />
          <input
            className="act-search-input"
            placeholder="Search activities, people, or places…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {loading ? (
          <div className="loading">Loading…</div>
        ) : filtered.length === 0 && search ? (
          <div className="activities-empty">
            <div className="activities-empty-icon"><Search size={36} color="#c3c5d9" /></div>
            <h3 className="activities-empty-title">No results</h3>
            <p className="activities-empty-sub">Try a different search term.</p>
          </div>
        ) : activities.length === 0 ? (
          <div className="activities-empty">
            <div className="activities-empty-icon"><MapPin size={36} color="#c3c5d9" /></div>
            <h3 className="activities-empty-title">No activities yet</h3>
            <p className="activities-empty-sub">Post your first plan and invite your crew.</p>
            <button className="btn-primary" style={{ borderRadius: 12, marginTop: 8 }} onClick={() => navigate('/create-activity')}>
              + New Activity
            </button>
          </div>
        ) : (
          <div className="activities-list">
            {filtered.map(activity => {
              const going = (activity.rsvps || []).filter(r => r.response_type === 'im_in');
              const myRsvp = (activity.rsvps || []).find(r => r.user_id === currentUserId);
              const isGoing = myRsvp?.response_type === 'im_in';
              const creator = activity.creator_display_name || 'Someone';

              const metaParts = [
                activity.start_time && formatDate(activity.start_time),
                activity.location_name,
                activity.duration_minutes && `${activity.duration_minutes} min`,
              ].filter(Boolean);

              return (
                <div
                  key={activity.id}
                  className="activity-card"
                  onClick={() => navigate(`/activity/${activity.id}`)}
                >
                  <div className="card-header-row">
                    <span className="card-type-chip">
                      <ActivityIcon type={activity.activity_type} size={13} color="#52AAD8" />
                      <span>{activity.activity_type}</span>
                    </span>
                    <span className="card-visibility-badge">{(activity.visibility_level || '').replace('_', ' ')}</span>
                  </div>

                  <h3 className="card-title">{activity.title || activity.activity_type}</h3>

                  {metaParts.length > 0 && (
                    <p className="card-meta">{metaParts.join(' · ')}</p>
                  )}

                  {activity.description && (
                    <p className="card-description">{activity.description}</p>
                  )}

                  <div className="card-footer">
                    <div className="card-creator">
                      <div className="creator-avatar">{getInitials(creator)}</div>
                      <span className="creator-footer-name">{creator} · {timeAgo(activity.created_at)}</span>
                    </div>

                    <div className="card-footer-right">
                      {going.length > 0 && (
                        <div className="card-going">
                          <div className="going-avatars">
                            {going.slice(0, 3).map((r, i) => (
                              <div key={i} className="going-avatar">{getInitials(r.display_name || '?')}</div>
                            ))}
                          </div>
                          <span className="going-text"><strong>{going.length}</strong></span>
                        </div>
                      )}

                      <button
                        className={`card-rsvp-btn ${isGoing ? 'active' : ''}`}
                        onClick={e => handleRSVP(e, activity.id, isGoing ? 'not_going' : 'im_in')}
                      >
                        {isGoing ? '✓ Going' : "I'm in"}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}

